import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { Calendar, Popover } from 'antd'
import { Icon } from '@iconify/react'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import styles from './DateRangePicker.module.css'
import triggerStyles from './TaskSingleDatePicker.module.css'

dayjs.extend(customParseFormat)

const DATE_PARSE_FORMATS = [
    'DD/MM/YY',
    'D/M/YY',
    'DD-MM-YY',
    'D-M-YY',
    'DD/MM/YYYY',
    'D/M/YYYY',
    'DD-MM-YYYY',
    'D-M-YYYY',
]

const DEFAULT_INPUT_DATE_FORMAT = 'DD/MM/YY'
const POPOVER_WIDTH_ESTIMATE = 280
const POPOVER_HEIGHT_ESTIMATE = 420
const VIEWPORT_EDGE_GAP = 8
const PLACEMENT_GAP = 4

const resolveBestPlacement = (triggerEl) => {
    if (!triggerEl || typeof window === 'undefined') return 'bottomLeft'

    const rect = triggerEl.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    const w = Math.min(POPOVER_WIDTH_ESTIMATE, vw - VIEWPORT_EDGE_GAP * 2)
    const h = Math.min(POPOVER_HEIGHT_ESTIMATE, vh - VIEWPORT_EDGE_GAP * 2)

    const candidates = [
        'bottomLeft',
        'bottomRight',
        'bottom',
        'topLeft',
        'topRight',
        'top',
        'rightTop',
        'rightBottom',
        'right',
        'leftTop',
        'leftBottom',
        'left',
    ]

    const getOrigin = (placement) => {
        switch (placement) {
            case 'bottomLeft':
                return { left: rect.left, top: rect.bottom + PLACEMENT_GAP }
            case 'bottomRight':
                return {
                    left: rect.right - w,
                    top: rect.bottom + PLACEMENT_GAP,
                }
            case 'bottom':
                return {
                    left: rect.left + rect.width / 2 - w / 2,
                    top: rect.bottom + PLACEMENT_GAP,
                }
            case 'topLeft':
                return { left: rect.left, top: rect.top - h - PLACEMENT_GAP }
            case 'topRight':
                return {
                    left: rect.right - w,
                    top: rect.top - h - PLACEMENT_GAP,
                }
            case 'top':
                return {
                    left: rect.left + rect.width / 2 - w / 2,
                    top: rect.top - h - PLACEMENT_GAP,
                }
            case 'rightTop':
                return { left: rect.right + PLACEMENT_GAP, top: rect.top }
            case 'rightBottom':
                return {
                    left: rect.right + PLACEMENT_GAP,
                    top: rect.bottom - h,
                }
            case 'right':
                return {
                    left: rect.right + PLACEMENT_GAP,
                    top: rect.top + rect.height / 2 - h / 2,
                }
            case 'leftTop':
                return { left: rect.left - w - PLACEMENT_GAP, top: rect.top }
            case 'leftBottom':
                return {
                    left: rect.left - w - PLACEMENT_GAP,
                    top: rect.bottom - h,
                }
            case 'left':
                return {
                    left: rect.left - w - PLACEMENT_GAP,
                    top: rect.top + rect.height / 2 - h / 2,
                }
            default:
                return { left: rect.left, top: rect.bottom + PLACEMENT_GAP }
        }
    }

    const scorePlacement = (placement) => {
        const { left, top } = getOrigin(placement)
        const overflowX =
            Math.max(0, VIEWPORT_EDGE_GAP - left) +
            Math.max(0, left + w - (vw - VIEWPORT_EDGE_GAP))
        const overflowY =
            Math.max(0, VIEWPORT_EDGE_GAP - top) +
            Math.max(0, top + h - (vh - VIEWPORT_EDGE_GAP))
        return overflowX + overflowY
    }

    let best = 'bottomLeft'
    let bestScore = Number.POSITIVE_INFINITY
    for (const placement of candidates) {
        const score = scorePlacement(placement)
        if (score < bestScore) {
            bestScore = score
            best = placement
            if (score === 0) break
        }
    }
    return best
}

const parseManualDate = (text) => {
    const trimmed = String(text ?? '').trim()
    if (!trimmed) return null

    for (const fmt of DATE_PARSE_FORMATS) {
        const parsed = dayjs(trimmed, fmt, true)
        if (parsed.isValid()) return parsed.startOf('day')
    }

    const loose = dayjs(trimmed)
    return loose.isValid() ? loose.startOf('day') : null
}

const toDayjs = (value) => {
    if (!value) return null
    const d = dayjs(value)
    return d.isValid() ? d.startOf('day') : null
}

/**
 * Single start/end date picker for task detail (Jira-style).
 * Does not modify the shared DateRangePicker.
 *
 * @param {'start' | 'end'} field
 * @param {{ start?: number|null, end?: number|null }} value
 * @param {(next: { start: number|null, end: number|null }) => void} onChange
 */
const TaskSingleDatePicker = ({
    field = 'start',
    value,
    onChange,
    placeholder = 'None',
    showClear = true,
    disabled = false,
    getPopupContainer,
    dateFormat,
}) => {
    const isDisabled = disabled || typeof onChange !== 'function'
    const isStartField = field === 'start'
    const inputDateFormat = dateFormat || DEFAULT_INPUT_DATE_FORMAT

    const formatInputDate = useCallback(
        (d) => (d?.isValid() ? d.format(inputDateFormat) : ''),
        [inputDateFormat]
    )

    const [open, setOpen] = useState(false)
    const [placement, setPlacement] = useState('bottomLeft')
    const [inputText, setInputText] = useState('')
    const [calendarViewDate, setCalendarViewDate] = useState(null)
    const panelRef = useRef(null)
    const triggerRef = useRef(null)
    const suppressBlurCommitRef = useRef(false)
    const calendarInteractionRef = useRef(false)

    const startDate = useMemo(() => toDayjs(value?.start), [value?.start])
    const endDate = useMemo(() => toDayjs(value?.end), [value?.end])
    const selectedDate = isStartField ? startDate : endDate

    const syncInputFromValue = useCallback(() => {
        setInputText(formatInputDate(selectedDate))
    }, [formatInputDate, selectedDate])

    useEffect(() => {
        if (open) {
            syncInputFromValue()
            setCalendarViewDate(null)
            setPlacement(resolveBestPlacement(triggerRef.current))
        }
    }, [open, syncInputFromValue])

    const handleOpenChange = useCallback(
        (nextOpen) => {
            if (isDisabled) {
                setOpen(false)
                return
            }
            if (nextOpen) {
                setPlacement(resolveBestPlacement(triggerRef.current))
            }
            setOpen(nextOpen)
        },
        [isDisabled]
    )

    const armCalendarInteraction = useCallback(() => {
        calendarInteractionRef.current = true
        window.setTimeout(() => {
            calendarInteractionRef.current = false
        }, 500)
    }, [])

    const shouldSkipBlurCommit = () =>
        suppressBlurCommitRef.current || calendarInteractionRef.current

    const emitChange = useCallback(
        (nextSelected) => {
            let nextStart = isStartField ? nextSelected : startDate
            let nextEnd = isStartField ? endDate : nextSelected

            if (
                nextStart &&
                nextEnd &&
                nextStart.isAfter(nextEnd, 'day')
            ) {
                if (isStartField) {
                    nextEnd = nextStart
                } else {
                    nextStart = nextEnd
                }
            }

            onChange?.({
                start: nextStart?.isValid() ? nextStart.valueOf() : null,
                end: nextEnd?.isValid() ? nextEnd.valueOf() : null,
            })
        },
        [isStartField, startDate, endDate, onChange]
    )

    const calendarPanelValue = useMemo(() => {
        if (calendarViewDate) return calendarViewDate
        if (selectedDate) return selectedDate
        if (isStartField && endDate) return endDate
        if (!isStartField && startDate) return startDate
        return dayjs()
    }, [calendarViewDate, selectedDate, isStartField, startDate, endDate])

    const handleDateSelect = (date, info) => {
        if (!date) return
        if (typeof date.isValid === 'function' && !date.isValid()) return
        const d = date.startOf('day')
        if (info?.source !== 'date') {
            setCalendarViewDate(d)
            return
        }

        setCalendarViewDate(d)
        setInputText(formatInputDate(d))
        emitChange(d)
        setOpen(false)
    }

    const handlePanelChange = (date) => {
        if (date && typeof date.isValid === 'function' && date.isValid()) {
            setCalendarViewDate(date.startOf('day'))
        }
    }

    const commitInputBlur = () => {
        if (shouldSkipBlurCommit()) return

        const trimmed = inputText.trim()
        if (!trimmed) {
            if (selectedDate) {
                setInputText(formatInputDate(selectedDate))
                return
            }
            emitChange(null)
            return
        }

        const parsed = parseManualDate(trimmed)
        if (!parsed) {
            setInputText(formatInputDate(selectedDate))
            return
        }

        if (!selectedDate?.isSame(parsed, 'day')) {
            emitChange(parsed)
        } else {
            setInputText(formatInputDate(selectedDate))
        }
    }

    const handleInputBlur = () => {
        window.setTimeout(() => {
            if (shouldSkipBlurCommit()) return
            if (panelRef.current?.contains(document.activeElement)) {
                return
            }
            commitInputBlur()
        }, 0)
    }

    const handleCalendarMouseDown = (e) => {
        if (e.target.closest('.ant-picker-cell')) {
            armCalendarInteraction()
        }
    }

    const disabledDate = (current) => {
        if (!current) return false
        if (isStartField && endDate) {
            return current.isAfter(endDate, 'day')
        }
        if (!isStartField && startDate) {
            return current.isBefore(startDate, 'day')
        }
        return false
    }

    const handleClear = (e) => {
        e.stopPropagation()
        emitChange(null)
        setInputText('')
        setOpen(false)
    }

    const displayText = useMemo(() => {
        if (!selectedDate) return null
        if (dateFormat) return selectedDate.format(dateFormat)
        const currentYear = dayjs().year()
        return selectedDate.year() === currentYear
            ? selectedDate.format('D MMM')
            : selectedDate.format('D MMM, YYYY')
    }, [selectedDate, dateFormat])

    const content = (
        <div ref={panelRef} className={styles.datePickerContainer}>
            <div className={styles.tabs} role="group" aria-label="Date">
                <div className={`${styles.tabField} ${styles.activeTab}`}>
                    <input
                        type="text"
                        className={styles.tabInput}
                        placeholder={
                            isStartField ? 'Start date' : 'End date'
                        }
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        onBlur={handleInputBlur}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur()
                        }}
                    />
                </div>
            </div>
            <div
                className={styles.calendarWrapper}
                onMouseDownCapture={handleCalendarMouseDown}
            >
                <Calendar
                    value={calendarPanelValue}
                    onSelect={handleDateSelect}
                    onPanelChange={handlePanelChange}
                    disabledDate={disabledDate}
                    className={styles.calendarPicker}
                    fullscreen={false}
                />
            </div>
        </div>
    )

    return (
        <Popover
            content={content}
            trigger={isDisabled ? [] : 'click'}
            open={isDisabled ? false : open}
            onOpenChange={handleOpenChange}
            placement={placement}
            autoAdjustOverflow
            align={{
                overflow: {
                    adjustX: true,
                    adjustY: true,
                },
            }}
            overlayClassName={styles.datePickerPopover}
            overlayStyle={{ maxWidth: 'calc(100vw - 24px)' }}
            getPopupContainer={getPopupContainer || (() => document.body)}
            destroyTooltipOnHide
        >
            <div
                ref={triggerRef}
                className={triggerStyles.triggerWrap}
                style={isDisabled ? { cursor: 'default' } : undefined}
            >
                {displayText ? (
                    <div className={triggerStyles.trigger}>
                        <span className={triggerStyles.iconSlot}>
                            <Icon
                                icon="akar-icons:calendar"
                                width={16}
                                height={16}
                                className={triggerStyles.icon}
                            />
                        </span>
                        <span className={triggerStyles.valueText}>
                            {displayText}
                        </span>
                        {showClear && !isDisabled ? (
                            <Icon
                                icon="mdi:close"
                                width={14}
                                height={14}
                                className={triggerStyles.clearIcon}
                                onClick={handleClear}
                            />
                        ) : null}
                    </div>
                ) : (
                    <div className={triggerStyles.trigger}>
                        <span
                            className={`${triggerStyles.iconSlot} ${triggerStyles.iconSlotEmpty}`}
                        >
                            <Icon
                                icon="akar-icons:calendar"
                                width={16}
                                height={16}
                                className={triggerStyles.iconMuted}
                            />
                        </span>
                        <span className={triggerStyles.placeholderText}>
                            {placeholder}
                        </span>
                    </div>
                )}
            </div>
        </Popover>
    )
}

export default TaskSingleDatePicker
